// Import pattern definitions from separate file
import { 
  patterns,
  emailPattern,
  phonePattern,
  ukPhonePattern,
  ssnPattern,
  creditCardPattern,
  addressPattern,
  namePattern,
  companyNamePattern,
  specificCompanyNamesPattern,
  passwordPattern,
  dobPattern,
  datePattern,
  creditCardAltPattern,
  bankAccountPattern,
  bankAccountNumberPattern,
  apiKeyPattern,
  stripeApiKeyPattern,
  transactionIdPattern,
  dollarAmountPattern,
  invoicePattern,
  ticketNumberPattern,
  ipAddressPattern,
  ipAddressWithPortPattern,
  envVarPattern
} from './patternDefinitions';

// Export patterns for backward compatibility
export { patterns };

// Masking functions for different types of sensitive data
export const maskEmail = (email) => {
  const [username, domain] = email.split('@');
  return `${username.charAt(0)}${'*'.repeat(username.length - 1)}@${domain}`;
};

export const maskCreditCard = (cardNumber) => {
  // Keep only the last 4 digits visible
  const parts = cardNumber.split(/[\s-]/);
  if (parts.length === 4) {
    return parts.map((part, index) => index < 3 ? '*'.repeat(part.length) : part).join(' ');
  }
  return cardNumber.replace(/\d(?=\d{4})/g, '*');
};

export const maskPassword = (match) => {
  const passwordMatch = /[Pp]assword(?:\s*is|\s*:)?\s*["']?([^"'\s,\.]{6,})["']?/.exec(match);
  if (passwordMatch && passwordMatch[1]) {
    return match.replace(passwordMatch[1], '*'.repeat(passwordMatch[1].length));
  }
  return '*'.repeat(match.length);
};

export const maskDate = (match) => {
  return match.replace(/\d+/g, match => '*'.repeat(match.length));
};

export const maskApiKey = (match) => {
  // For Stripe-like API keys (sk_live_xxx, pk_test_xxx)
  const stripeKeyMatch = /\b((?:sk|pk|rk)_(?:test|live)_)([a-zA-Z0-9]+)\b/.exec(match);
  if (stripeKeyMatch) {
    const prefix = stripeKeyMatch[1]; // Keep sk_live_ or pk_test_ part
    const rest = stripeKeyMatch[2];
    return match.replace(stripeKeyMatch[0], prefix + '*'.repeat(rest.length));
  }
  
  // For general API keys mentioned in text
  const apiKeyMatch = /\b(?:api key|API key|key)(?:\s*is|\s*:)?\s*["'=]?([a-zA-Z0-9_]{20,})["'=]?/i.exec(match);
  if (apiKeyMatch && apiKeyMatch[1]) {
    return match.replace(apiKeyMatch[1], '*'.repeat(apiKeyMatch[1].length));
  }
  
  return '*'.repeat(match.length);
};

export const maskTransactionId = (match) => {
  const txIdMatch = /\b(?:transaction|tx|transaction id|tx id|transaction ID|TX ID)(?:\s*is|\s*:|\s*#)?\s*(\d{4,8})\b/i.exec(match);
  if (txIdMatch && txIdMatch[1]) {
    return match.replace(txIdMatch[1], '*'.repeat(txIdMatch[1].length));
  }
  return match;
};

export const maskDollarAmount = (match) => {
  // Replace all digits in dollar amounts with asterisks, but keep the dollar sign and commas
  return match.replace(/\d/g, '*');
};

export const maskInvoice = (match) => {
  // Keep the "INV-" prefix but mask the numbers
  return match.replace(/(\b(?:INV|inv)[-\s]?)(\d+)/g, (_, prefix, numbers) => {
    return prefix + '*'.repeat(numbers.length);
  });
};

export const maskTicketNumber = (match) => {
  // Keep the "ticket #" part but mask the numbers
  return match.replace(/(\b(?:ticket|Ticket)(?:\s*#|\s*number|\s*ID|\s*:)?\s*)(\d+)/gi, (_, prefix, numbers) => {
    return prefix + '*'.repeat(numbers.length);
  });
};

export const maskUkPhone = (match) => {
  // Keep the +44 prefix but mask the rest
  return match.replace(/(\+44\s)(\d{4}\s\d{6})/g, (_, prefix, numbers) => {
    return prefix + '*'.repeat(numbers.length);
  });
};

export const maskCompanyName = (match) => {
  // Mask the company name
  return '*'.repeat(match.length);
};

export const maskSpecificCompanyName = (match) => {
  // Mask specific company names
  return '*'.repeat(match.length);
};

export const maskIpAddress = (match) => {
  // Mask IP address but keep the format (x.x.x.x)
  return match.replace(/\d+/g, match => '*'.repeat(match.length));
};

export const maskIpAddressWithPort = (match) => {
  // Mask IP address with port but keep the format (x.x.x.x:port)
  const [ip, port] = match.split(':');
  return maskIpAddress(ip) + ':' + '*'.repeat(port.length);
};

export const maskEnvVar = (match) => {
  // Regex to capture name and value: ([A-Z0-9_]*(?:SECRET|PASSWORD|TOKEN|KEY|AUTH|CREDENTIAL|PRIVATE)[A-Z0-9_]*)=(.*)
  const parts = match.match(/\b([A-Z0-9_]*(?:SECRET|PASSWORD|TOKEN|KEY|AUTH|CREDENTIAL|PRIVATE)[A-Z0-9_]*)=(.*)/i);
  if (parts && parts.length === 3) {
    const varName = parts[1]; // The variable name, e.g., MY_SECRET_KEY
    const value = parts[2];   // The value part
    return `${varName}=${'*'.repeat(value.length)}`;
  }
  // If the input string doesn't match the sensitive env var pattern, return it as is.
  return match; 
};

export const maskDefault = (match) => {
  return '*'.repeat(match.length);
};

// Main masking function
export const maskText = (text) => {
  if (!text) return '';
  
  // Create a copy of the text to work with
  let result = text;
  
  // Apply each pattern in a specific order to ensure proper masking
  // First mask emails
  result = result.replace(emailPattern, match => maskEmail(match));
  
  // Then mask credit cards
  result = result.replace(creditCardPattern, match => maskCreditCard(match));
  result = result.replace(creditCardAltPattern, match => maskCreditCard(match));
  
  // Then mask phone numbers
  result = result.replace(phonePattern, match => maskDefault(match));
  
  // Then mask SSNs
  result = result.replace(ssnPattern, match => maskDefault(match));
  
  // Then mask addresses
  result = result.replace(addressPattern, match => maskDefault(match));
  
  // Then mask names
  result = result.replace(namePattern, match => maskDefault(match));
  
  // Then mask passwords
  result = result.replace(passwordPattern, match => maskPassword(match));
  
  // Then mask DOB and dates
  result = result.replace(dobPattern, match => maskDate(match));
  result = result.replace(datePattern, match => maskDate(match));
  
  // Then mask bank account numbers
  result = result.replace(bankAccountPattern, match => maskDefault(match));
  result = result.replace(bankAccountNumberPattern, match => maskDefault(match));
  
  // Then mask API keys
  result = result.replace(apiKeyPattern, match => maskApiKey(match));
  result = result.replace(stripeApiKeyPattern, match => maskApiKey(match));
  
  // Then mask transaction IDs
  result = result.replace(transactionIdPattern, match => maskTransactionId(match));
  
  // Then mask dollar amounts
  result = result.replace(dollarAmountPattern, match => maskDollarAmount(match));
  
  // Then mask invoice numbers
  result = result.replace(invoicePattern, match => maskInvoice(match));
  
  // Then mask ticket numbers
  result = result.replace(ticketNumberPattern, match => maskTicketNumber(match));
  
  // Then mask UK phone numbers
  result = result.replace(ukPhonePattern, match => maskUkPhone(match));
  
  // Then mask company names
  result = result.replace(companyNamePattern, match => maskCompanyName(match));
  
  // Then mask specific company names
  result = result.replace(specificCompanyNamesPattern, match => maskSpecificCompanyName(match));
  
  // Then mask IP addresses with ports (must come before regular IP addresses)
  result = result.replace(ipAddressWithPortPattern, match => maskIpAddressWithPort(match));
  
  // Then mask IP addresses
  result = result.replace(ipAddressPattern, match => maskIpAddress(match));

  // Then mask sensitive environment variables
  result = result.replace(envVarPattern, match => maskEnvVar(match));
  
  return result;
};
